import { __assign, __awaiter, __generator, __values } from "tslib";
import { mapEventMetadata, NovaEventingProvider, NovaGraphQLProvider, } from '@nova/react';
import { AppBarEventTypes, } from '@office-start/appbar-events';
import { getSanitizedAppId, isAppPinned, moveApp, pinApp, unpinApp, } from '@office-start/data-resolver-app-pinning';
import { NovaDataContext, NovaDataProvider, } from '@office-start/data-resolver-appbar';
import { logClickEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents } from '@office-start/instrumentation-types';
import { logUserAction } from '@office-start/telemetry-service';
import * as React from 'react';
import { createNovaEventHandler } from './NovaEventHandler';
import { useNovaGraphQLQuery } from './useNovaGraphQLQuery';
var NovaContextProviderRenderer = function (_a) {
    var children = _a.children, appbarConnectorService = _a.appbarConnectorService;
    var _b = React.useContext(NovaDataContext), tileData = _b.tileData, userData = _b.userData, setUserData = _b.setUserData;
    var launchTile = function (tileID) {
        var e_1, _a;
        var userClickTime = Date.now();
        var apps = tileData === null || tileData === void 0 ? void 0 : tileData.m365Apps;
        if (apps === null || apps === void 0 ? void 0 : apps.edges) {
            try {
                for (var _b = __values(apps.edges), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var tile = _c.value;
                    if (tile.node.id) {
                        var isLaunchedTile = tileID != null && tileID === tile.node.id;
                        if (isLaunchedTile) {
                            appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.onAppButtonClick(userClickTime, !!tile.node.isInUse && isLaunchedTile);
                        }
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
    };
    var getAppLoggingInfo = function (applicationID) {
        var e_2, _a;
        var apps = tileData === null || tileData === void 0 ? void 0 : tileData.m365Apps;
        try {
            for (var _b = __values(apps.edges), _c = _b.next(); !_c.done; _c = _b.next()) {
                var tile = _c.value;
                if (tile.node.id === applicationID) {
                    var platformType = tile.node.platformType;
                    var sanitizedAppId = getSanitizedAppId(applicationID, platformType);
                    return { sanitizedAppId: sanitizedAppId, platformType: platformType };
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return {};
    };
    var pinTile = function (applicationID) {
        if (!userData || !setUserData) {
            return;
        }
        var _a = getAppLoggingInfo(applicationID), sanitizedAppId = _a.sanitizedAppId, platformType = _a.platformType;
        if (sanitizedAppId) {
            logClickEvent(KnownInstrumentationEvents.AppBarPinApp, sanitizedAppId, 'AppBar');
            // TODO: Remove above Aria Click logging once OTEL migration complete
            logUserAction({
                eventName: KnownInstrumentationEvents.AppBarPinApp,
                type: 'Click',
                id: sanitizedAppId,
                area: 'AppBar',
                result: 'Pin',
                target: 'AppBar',
            });
        }
        var newUserData = pinApp(userData, applicationID, platformType, 'UserPinned');
        setUserData(newUserData);
    };
    var unpinTile = function (applicationID) {
        if (!userData || !setUserData) {
            return;
        }
        var sanitizedAppId = getAppLoggingInfo(applicationID).sanitizedAppId;
        if (sanitizedAppId) {
            logClickEvent(KnownInstrumentationEvents.AppBarUnpinApp, sanitizedAppId, 'AppBar');
            // TODO: Remove above Aria Click logging once OTEL migration complete
            logUserAction({
                eventName: KnownInstrumentationEvents.AppBarUnpinApp,
                type: 'Click',
                id: sanitizedAppId,
                area: 'AppBar',
                result: 'Unpin',
                target: 'AppBar',
            });
        }
        var newUserData = unpinApp(userData, applicationID);
        setUserData(newUserData);
    };
    var movePinnedTile = function (applicationID, updatedIndex) {
        if (!userData || !setUserData) {
            return;
        }
        var _a = getAppLoggingInfo(applicationID), sanitizedAppId = _a.sanitizedAppId, platformType = _a.platformType;
        if (sanitizedAppId) {
            logClickEvent(KnownInstrumentationEvents.AppBarMovePinnedApp, sanitizedAppId, 'AppBar');
            // TODO: Remove above Aria Click logging once OTEL migration complete
            logUserAction({
                eventName: KnownInstrumentationEvents.AppBarMovePinnedApp,
                type: 'Click',
                id: sanitizedAppId,
                area: 'AppBar',
                result: 'Move',
                target: 'AppBar',
            });
        }
        var newUserData = isAppPinned(userData, applicationID)
            ? moveApp(userData, applicationID, updatedIndex)
            : moveApp(
            // If the app is not already pinned, pin the app first before moving the app
            pinApp(userData, applicationID, platformType, 'UserPinned'), applicationID, updatedIndex);
        setUserData(newUserData);
    };
    var eventHandler = createNovaEventHandler();
    var novaEventing = {
        bubble: function (event) {
            return __awaiter(this, void 0, void 0, function () {
                var data;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            // If this is a tile launch event, then trigger the selection of the tile
                            if (event.event.type === AppBarEventTypes.launch &&
                                event.event.data) {
                                data = event.event.data();
                                launchTile(data.tileID);
                            }
                            return [4 /*yield*/, eventHandler.bubble(event)];
                        case 1:
                            _a.sent();
                            appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.updateCurrentAppVerticalPosition();
                            return [2 /*return*/];
                    }
                });
            });
        },
    };
    var novaGraphQl = useNovaGraphQLQuery(tileData, pinTile, unpinTile, movePinnedTile);
    return (React.createElement(NovaEventingProvider, { eventing: novaEventing, reactEventMapper: mapEventMetadata },
        React.createElement(NovaGraphQLProvider, { graphql: novaGraphQl }, children)));
};
export var NovaContextProvider = function (props) {
    return (React.createElement(NovaDataProvider, null,
        React.createElement(NovaContextProviderRenderer, __assign({}, props))));
};
NovaContextProvider.displayName = 'NovaContextProvider';
//# sourceMappingURL=NovaContextProvider.js.map